import PropTypes from 'prop-types';
import React, { useContext } from 'react';

import LookupField from '../../ui/lookup/LookupField';
import { DateTimeRenderer, TextAlignLeftRenderer } from '../SmartTableRenderers';

import ApplicationContext from '../../ApplicationContext';
import { TableColumn } from '../../tableSettings';
import { SQLIndex } from '../../fms.core/constants';
import { LookupCopyInvRefNo } from '../../fms.data/lookup';
import { JobNames } from '../../framework.core/JobNames';

type columnType =  'M'|'H'|'W';

function CopyInvTargetBLLookupField({
  initialValue = '',
  onSelect = () => {},
  placeholder = undefined,
  onTextChange = () => {},
  disabled = false,
  lookupId = undefined,
  onTabNext = undefined,
  onTabPrevious = undefined,
  maxLength = undefined,
  jobType
}: {
  disabled?: boolean,
  initialValue: string,
  onSelect: (x:LookupCopyInvRefNo) => void,
  placeholder: string|undefined,
  onTextChange: (text:string|undefined) => void,
  lookupId?: string|undefined,
  onTabNext?: (() => boolean)|undefined,
  onTabPrevious?: (() => boolean)|undefined,
  maxLength?: number|undefined,
  jobType: JobNames,
}) {
  const appContext = useContext(ApplicationContext);

  async function queryCopyInvRefNo(queryString:string|null) {
    const apiResult = await appContext.api.getLookupItems(SQLIndex.CopyInvRefNos, queryString ?? '', jobType);
    return apiResult?.entities;
  }
  function onCancel() {
  }

  function filter(data:Array<LookupCopyInvRefNo>, filterText:string) {
    return data.filter(x =>
      (x.refNo && x.refNo.toLowerCase().startsWith(filterText.toLowerCase()))||
      (x.mblNo && x.mblNo.toLowerCase().startsWith(filterText.toLowerCase()))||
      (x.entity && x.entity.toLowerCase().startsWith(filterText.toLowerCase()))||
      (x.vessel && x.vessel.toLowerCase().startsWith(filterText.toLowerCase()))
      );
  }


  let mblLabel, hblLabel, entityLabel, vesselLabel;

  let type: columnType = 'M';

  switch(jobType){
    case JobNames.OIMbl:
    case JobNames.OEMbl:
      mblLabel = 'Master B/L #';
      entityLabel = 'Agent';
      vesselLabel = "Vessel Name";
      type='M';
      break;
    case JobNames.AIMbl:
    case JobNames.AEMbl:
      mblLabel = 'MAWB #';
      entityLabel = 'Agent';
      vesselLabel = 'Flight #';
      type='M';
      break;
    case JobNames.OIHbl:
    case JobNames.OEHbl:
    case JobNames.OtherOP:
      mblLabel = 'Master B/L #';
      hblLabel = 'House B/L #';
      entityLabel = 'Carrier';
      type = 'H';
      break;
    case JobNames.AIHbl:
    case JobNames.AEHbl:
      mblLabel = 'MAWB #';
      hblLabel = 'HAWB #';
      entityLabel = 'Carrier';
      type='H';
      break;
    case JobNames.WHRcv:
      mblLabel = 'Customer (W/H)';
      hblLabel = 'Ship From';
      entityLabel = 'Product Code/SKU';
      type='W';
      break;
    case JobNames.WHShip:
      mblLabel = 'Customer (W/H)';
      hblLabel = 'Ship To';
      entityLabel = 'Product Code/SKU';
      type='W';
      break;
  }


  const MBLcolumns:Array<TableColumn> = [
    { Header: 'Ref. #', accessor: 'refNo', resizable: false, sortable: false, canBeHidden: false, visible: true, width: 100, Cell: TextAlignLeftRenderer },
    { Header: mblLabel, accessor: 'mblNo', resizable: false, sortable: false, canBeHidden: false, visible: true, width: 100, Cell: TextAlignLeftRenderer },
    { Header: entityLabel, accessor: 'entity', resizable: false, sortable: false, canBeHidden: false, visible: true, width: 100, Cell: TextAlignLeftRenderer },
    { Header: vesselLabel, accessor: 'vessel', resizable: false, sortable: false, canBeHidden: false, visible: true, width: 100, Cell: TextAlignLeftRenderer },
    { Header: 'Create Date', accessor: 'createDate', resizable: false, sortable: false, canBeHidden: false, visible: true, width: 100, Cell: DateTimeRenderer },
  ];

  const HBLcolumns:Array<TableColumn> = [
    { Header: 'Ref. #', accessor: 'refNo', resizable: false, sortable: false, canBeHidden: false, visible: true, width: 100, Cell: TextAlignLeftRenderer },
    { Header: mblLabel, accessor: 'mblNo', resizable: false, sortable: false, canBeHidden: false, visible: true, width: 100, Cell: TextAlignLeftRenderer },
    { Header: hblLabel, accessor: 'hblNo', resizable: false, sortable: false, canBeHidden: false, visible: true, width: 100, Cell: TextAlignLeftRenderer },
    { Header: entityLabel, accessor: 'entity', resizable: false, sortable: false, canBeHidden: false, visible: true, width: 100, Cell: TextAlignLeftRenderer },
    { Header: 'Create Date', accessor: 'createDate', resizable: false, sortable: false, canBeHidden: false, visible: true, width: 100, Cell: DateTimeRenderer },
  ];

  const WHcolumns:Array<TableColumn> = [
    { Header: 'Ref. #', accessor: 'refNo', resizable: false, sortable: false, canBeHidden: false, visible: true, width: 100, Cell: TextAlignLeftRenderer },
    { Header: mblLabel, accessor: 'mblNo', resizable: false, sortable: false, canBeHidden: false, visible: true, width: 100, Cell: TextAlignLeftRenderer },
    { Header: hblLabel, accessor: 'hblNo', resizable: false, sortable: false, canBeHidden: false, visible: true, width: 100, Cell: TextAlignLeftRenderer },
    { Header: entityLabel, accessor: 'entity', resizable: false, sortable: false, canBeHidden: false, visible: true, width: 100, Cell: TextAlignLeftRenderer },
    { Header: 'Create Date', accessor: 'createDate', resizable: false, sortable: false, canBeHidden: false, visible: true, width: 100, Cell: DateTimeRenderer },
  ];

  return (
    <LookupField
      lookupId={lookupId}
      onTabNext={onTabNext}
      onTabPrevious={onTabPrevious}
      disabled={disabled}
      query={queryCopyInvRefNo}
      width="520px"
      columns={type === 'M' ? MBLcolumns : (type === 'H' ? HBLcolumns : WHcolumns)} 
      onSelect={onSelect}
      onCancel={onCancel}
      labelProp="refNo"
      initialValue={initialValue}
      placeholder={placeholder}
      onTextChange={onTextChange}
      filter={filter}
      maxLength={maxLength}
      />
  );
}

CopyInvTargetBLLookupField.defaultProps = {
  onSelect: () => {},
  onTextChange: () => {},
  disabled: false,
};

CopyInvTargetBLLookupField.propTypes = {
  initialValue: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onTextChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool.isRequired,
  lookupId: PropTypes.string,
  onTabNext: PropTypes.func,
  onTabPrevious: PropTypes.func,
  maxLength: PropTypes.number,
  jobType: PropTypes.string,
};


export default React.memo(CopyInvTargetBLLookupField);
